import { isSameCity, matchesLocality } from './cityMatching';
import { type LocalityLookup } from './useLocalitiesForPostalCode';

// @ai-generated 2026-09-21T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5

/**
 * The localities of a postal code, put up for the user to take one. The single way the automation
 * ever offers a choice: a postal code with several localities and a postal code that contradicts
 * the city are the same question — which of these places is it — and they are asked the same way.
 */
export interface CityOffer {
    /** The localities on offer, one button each. */
    localities: string[];

    /**
     * The city in the record the offer contradicts, if there is one. Its presence is what turns a
     * convenience into a warning: an entry is about to be replaced, so it is stated plainly and
     * keeping it is one of the answers.
     */
    contested: string | undefined;
}

/**
 * How a line reads. `progress` reports work in progress: it is shown but not announced, being a side
 * effect of typing with nothing in it to act on.
 */
export type CityNoticeTone = 'progress' | 'neutral' | 'success';

/**
 * What the automation has to say about the city — either a line of state, or the localities of the
 * postal code with an invitation to pick one.
 */
export type CityNotice =
    | { kind: 'line'; tone: CityNoticeTone; text: string }
    | { kind: 'offer'; text: string; offer: CityOffer };

/** Everything a notice is derived from. */
export interface CityNoticeInput {
    lookup: LocalityLookup;

    /**
     * The localities the city field offers in its own suggestion list, empty where it offers none.
     * The notice follows it rather than counting again, so that what is said below the field and
     * what can be picked in it can never come apart.
     */
    offered: string[];

    /** What the field shows, or is one render from showing — see `pendingCity`. */
    city: string;

    /** True while that city is one the automation wrote — see `isCityOurs`. */
    isCityOurs: boolean;

    /** The city the user kept against the postal code, if they have. */
    keptCity: string | undefined;

    /** True once the user has typed in one of the two fields. */
    hasEdited: boolean;
}

export type Translate = (text: string) => string;

function line(tone: CityNoticeTone, text: string): CityNotice {
    return { kind: 'line', tone, text };
}

function offer(text: string, localities: string[], contested: string | undefined): CityNotice {
    return { kind: 'offer', text, offer: { localities, contested } };
}

/**
 * What the localities of the postal code mean for the city in the field — the entire visible
 * behaviour of the automation, in one pure function.
 *
 * It says how the two stand to one another whenever it can, an address that is merely being looked
 * at included. A saved address is precisely where the confirmation is worth having, and saving is
 * no reason for it to disappear: the frame reloads the record after a save, which makes every
 * address a freshly opened one within moments of the automation having filled the city in.
 *
 * The one thing it keeps to itself is the contradiction, and it keeps it in two cases. Not about a
 * city of the automation's own making, which is about to be put right and is no evidence of
 * anything. And not until the user has typed in one of the two fields: somebody saved that
 * combination and locality data is only a second opinion, so being contradicted for opening a
 * record is an accusation nobody asked for.
 */
export function describeCity(input: CityNoticeInput, i18n: Translate): CityNotice | undefined {
    switch (input.lookup.status) {
        case 'idle':
            return undefined;
        case 'searching':
            return line('progress', i18n('Orte werden gesucht …'));
        case 'unavailable':
            return line('neutral', i18n('Vorschläge gerade nicht verfügbar'));
        case 'unknown':
            return line('neutral', i18n('Kein Ort zu dieser PLZ'));
        case 'ready':
            return describeAgainstLocalities(input, i18n);
        default: {
            // Every status has to decide what it says. Leaving one out fails the build here instead
            // of silently saying nothing — the one outcome that cannot be spotted on screen.
            const unhandled: never = input.lookup.status;
            return unhandled;
        }
    }
}

function describeAgainstLocalities(
    { lookup, offered, city, isCityOurs, keptCity, hasEdited }: CityNoticeInput,
    i18n: Translate,
): CityNotice | undefined {
    const { localities, zip } = lookup;

    if (city === '') {
        // A choice belongs in the field's own suggestion list, which is where the cursor is put for
        // it — repeating it as buttons would ask the same question twice. So the line only says how
        // many there are to pick from.
        if (offered.length > 0) {
            return line('neutral', `${offered.length} ${i18n('Orte zu dieser PLZ')}`);
        }
        if (localities.length === 0) {
            return undefined;
        }
        // Nothing offered but a locality to be had: the one the postal code leaves no choice about,
        // which gets no list. The click is here instead, and it is needed — the automation may not
        // fill every empty field by itself.
        return offer(i18n('Ort zu dieser PLZ'), localities, undefined);
    }

    if (matchesLocality(localities, city)) {
        // Saying it fits would be congratulating itself where the automation wrote the city.
        return isCityOurs
            ? line('neutral', i18n('Aus der PLZ übernommen'))
            : line('success', `${i18n('Passt zur PLZ')} ✓`);
    }

    if (isCityOurs) {
        // The automation's own city, and not this postal code's: it is one render from being put
        // right, and disputing it in between would flash a box that the next render withdraws.
        return undefined;
    }

    if (!hasEdited) {
        // The one thing an address that has only been opened is spared — see describeCity.
        return undefined;
    }

    if (isSameCity(keptCity, city)) {
        // Asked and answered: repeating it would leave a warning standing that the user has already
        // settled and can do nothing further about.
        return undefined;
    }

    return offer(`„${city}“ ${i18n('passt nicht zu PLZ')} ${zip}.`, localities, city);
}
